import React, { Fragment, MouseEventHandler, useState } from 'react';
import { Activity } from 'sr-types/lib/production/v1/graphql';
import { LabelValueDisplay } from '../../attributes/AttributeValue';
import { SECURITY_ACTIONS, useSecurity } from '../../common/auth/Security';
import { VerticalSeparator } from '../../common/components/DelimitedLabels';
import EllipsisText from '../../common/components/EllipsisText';
import { PLACEHOLDER_CONSTANTS } from '../../common/constants/constants';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { useBrowserOrAccountLocale } from '../../common/hooks/useBrowserOrAccountLocale';
import { useIsWithinBreakpoints } from '../../common/hooks/useIsWithinBreakpoints';
import { useI18n } from '../../common/i18n/I18n';
import { V } from '../../common/Layout';
import ResponsiveLayout from '../../common/ResponsiveLayout';
import { getStringFromDateRange } from '../helpers/activityUtils';
import AddMore from './AddMore';
import NoContent from './NoContent';
import NoValueAvailable from './NoValueAvailable';

type KeyDatesProps = {
    activities: Activity[];
    onAdd?: MouseEventHandler<HTMLButtonElement>;
    onEdit: (id: string) => void;
    isSaving?: boolean;
    isReadOnly?: boolean;
};

const KeyValuePreview = ({ name, value, addDate, canAddKeyDates }) => {
    return (
        name && (
            <V
                sx={{
                    width: 'fit-content',
                    alignItems: 'flex-start',
                    gap: 0.5
                }}
            >
                <>
                    {value ? (
                        <LabelValueDisplay label={name} value={value} />
                    ) : (
                        <>
                            <EllipsisText text={name} sx={{ fontSize: 'small', color: 'text.disabled' }} />
                            {canAddKeyDates ? (
                                <AddMore i18nToken="form.activity.addDate" onAdd={addDate} />
                            ) : (
                                PLACEHOLDER_CONSTANTS.noDataToPreview
                            )}
                        </>
                    )}
                </>
            </V>
        )
    );
};

export default function KeyDates({ activities, onAdd, onEdit, isSaving = false, isReadOnly = false }: KeyDatesProps) {
    const isMdOrBelow = useIsWithinBreakpoints(['xs', 's', 'm']);
    const locale = useBrowserOrAccountLocale();
    const t = useI18n();
    const [lastVisibleIndex, setLastVisibleIndex] = useState<number | undefined>();

    const { allowed: canEdit } = useSecurity(SECURITY_ACTIONS.EDIT, {
        entityName: ENTITY_CONSTANTS.production
    });
    const canAddKeyDates = !isReadOnly && canEdit;

    return Array.isArray(activities) && activities.length ? (
        <ResponsiveLayout
            gap={1.5}
            sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0 }}
            tileWidth={isMdOrBelow ? 125 : 140}
            setLastElementToFitIndex={setLastVisibleIndex}
            fixedEndElement={
                canAddKeyDates ? (
                    <AddMore i18nToken="production.header.activities.addKeyDates" onAdd={onAdd} disabled={isSaving} />
                ) : undefined
            }
        >
            {activities.map((activity: Activity, index) => {
                const isInOverflow = lastVisibleIndex !== undefined && index > lastVisibleIndex;
                const showSeparator = index < activities.length - 1 && !isInOverflow;
                return (
                    <Fragment key={activity?.identity?.id ?? index}>
                        <KeyValuePreview
                            name={activity?.name}
                            value={`${getStringFromDateRange(activity?.dateRange, locale)}`}
                            addDate={() => {
                                onEdit(activity?.identity?.id);
                            }}
                            canAddKeyDates={canAddKeyDates}
                        />
                        {showSeparator && <VerticalSeparator />}
                    </Fragment>
                );
            })}
        </ResponsiveLayout>
    ) : isReadOnly ? (
        <NoValueAvailable text={t('production.header.keyActivity.notAvailable')} />
    ) : canAddKeyDates ? (
        <NoContent
            containerSx={{
                g: 1,
                justifyContent: 'center',
                width: '220px',
                height: '43px',
                color: 'primary.main',
                '&:hover': {
                    cursor: 'pointer'
                }
            }}
            i18nToken="production.header.activities.addKeyDates"
            onAdd={onAdd}
            disabled={isSaving}
        />
    ) : null;
}
